import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { IUserSettings } from "../Models/IUserSettings";
import { IUnitConfiguration } from "../Models/IUnitConfiguration";
import { Constants } from "../Models/Constants";

// Define types for state setter functions
type StateSetterFunction = (value: string) => void;

/**
 * Get preferred units
 *
 * @export
 * @return {IUserSettings} 
 */
export function getPreferredUnits(): IUserSettings {
    const settingsJson = getLocalStorageItem('userSettings');
    if (!settingsJson) {
        return { preferredUnits: 'metric' };
    }
    
    try {
        const settings: IUserSettings = JSON.parse(settingsJson);
        if (settings && settings.preferredUnits) {
            settings.preferredUnits = settings.preferredUnits.toLowerCase();
        }
        return settings || { preferredUnits: 'metric' };
    } catch {
        return { preferredUnits: 'metric' };
    }
}

/**
 * Set temporary state for a component 
 *
 * @export
 * @param {string} value
 * @param {StateSetterFunction} handle
 * @param {number} [timeout=1500]
 */
export function setTemporaryState(value: string, handle: StateSetterFunction, timeout: number = 1500): void {
    setTimeout(() => {
        setTimeout(() => {
            handle('')
        }, timeout)
        handle(value);
    }, 0);
}

export function getFlowTypeUnits(flowType: string): string {
    const units = getPreferredUnits();
    if (units.preferredUnits === 'us') {
        if (flowType === 'Volumetric' || flowType === 'volumetric')
            return 'GPM';
        return 'lb/h'
    }

    if (flowType === 'Volumetric' || flowType === 'volumetric')
        return 'm3/h';

    return 'kg/h';
}

export function setRoles(newRoles: string[]): void {
    roles = newRoles;
}

export function getRoles(): string[] {
    return roles || [];
}

// Check if the user has a role
export function hasRole(...anyOfRoles: string[]): boolean {
    const currentRoles = getRoles();
    for (let i = 0; i < anyOfRoles.length; i++) {
        if (currentRoles.includes(anyOfRoles[i])) {
            return true;
        }
    }
    return false;
}

export function getSettingsWasSet(): boolean {
    return settingsWasSet || false;
}

export function setUserSettings(settings: IUserSettings): void {
    userSettings = settings;
    settingsWasSet = true;
}

export function setFeatures(input: Record<string, boolean>): void {
    features = input;
}

export function getFeatures(): Record<string, boolean> {
    return features || {};
}

export function setLocalStorageItem(name: string, value: string | number | boolean | object): void {
    if (value !== null && value !== undefined && value !== 'null') {
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        window.localStorage.setItem(`EMELIE_${name}`, stringValue);
    }
}

export function getLocalStorageItem(name: string): string | null {
    return window.localStorage.getItem(`EMELIE_${name}`);
}

export function setUserFirstname(name: string): void {
    userFirstname = name;
}

export function getUserFirstname(): string {
    return userFirstname || '';
}

export function setUserLastname(name: string): void {
    userLastname = name;
}

export function getUserLastname(): string {
    return userLastname || '';
}

export function deCapitalizeFirstLetter(input: string): string {
    return input.charAt(0).toLowerCase() + input.slice(1);
}

export function getAppInsightsConnectionString(): string | undefined {
    return process.env['REACT_APP_APPINSIGHTS_CONNECTIONSTRING'];
}

export function setAppInsights(appInsights: ApplicationInsights): void {
    if (__APP_INSIGHTS__ === undefined) {
        __APP_INSIGHTS__ = appInsights;
    }
}

export function getAppInsights(): ApplicationInsights | undefined {
    return __APP_INSIGHTS__;
}

export function setHomeLocation(location: string): void {
    setLocalStorageItem('EMELIE_homeLocation', location);
}

export function getHomeLocation(): string | null {
    return getLocalStorageItem('EMELIE_homeLocation');
}

/**
 * Set the country code in localStorage
 * 
 * @export
 * @param {string} country Country code (e.g., 'PL', 'SE', 'US')
 */
export function setCountry(country: string): void {
    if (country) {
        setLocalStorageItem('country', country.toUpperCase().trim());
    }
}

/**
 * Get the country code from localStorage
 * 
 * @export
 * @return {string | null} Country code or null if not set
 */
export function getCountry(): string | null {
    return getLocalStorageItem('country');
}

// Global variable declarations
declare global {
    var __USER_SETTINGS__: IUserSettings | undefined;
    var __SETTINGS_WAS_SET__: boolean | undefined;
    var __ROLES__: string[] | undefined;
    var __FEATURES__: Record<string, boolean> | undefined;
    var __USER_FIRSTNAME__: string | undefined;
    var __USER_LASTNAME__: string | undefined;
    var __APP_INSIGHTS__: ApplicationInsights | undefined;
}

let userSettings = window.__USER_SETTINGS__;
let roles = window.__ROLES__;
let settingsWasSet = window.__SETTINGS_WAS_SET__;
let features = window.__FEATURES__;
let userFirstname = window.__USER_FIRSTNAME__;
let userLastname = window.__USER_LASTNAME__;
let __APP_INSIGHTS__ = window.__APP_INSIGHTS__;